/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { LegacyPortalAccountService } from "@spwn-portal/grpc";
import { useI18n } from "hooks/i18n/i18n";
import { useEffect, useMemo, useState } from "react";
import { createCrossOriginPrivateConnectClient } from "utility/connectWeb";

import {
  RETURN_PATH_STORAGE_KEY,
  startggConfig,
  startggRegisterUrl,
} from "./startggConfig";
import { useStartggEntry } from "./useStartggEntry";

import type { StartggEntryStatus } from "./useStartggEntry";
import type { FC } from "react";

type Props = {
  /** 購入可否の判定に使うため、呼び出し側へ状態を渡す */
  onStatusChange: (status: StartggEntryStatus) => void;
};

export const StartggEntryVerification: FC<Props> = ({ onStatusChange }) => {
  const { t } = useI18n();
  const classes = styles();
  const { status, link, clear, recheck, isChecking } = useStartggEntry();
  const [linkError, setLinkError] = useState(false);
  const client = useMemo(
    () => createCrossOriginPrivateConnectClient(LegacyPortalAccountService),
    []
  );

  useEffect(() => {
    onStatusChange(status);
  }, [onStatusChange, status]);

  const startLink = async () => {
    setLinkError(false);
    try {
      // 認可 URL は backend が state 付きで組み立てる。state は HttpOnly cookie に入り、
      // 戻ってきたときに backend 側で照合される
      const res = await client.startStartggLink({
        redirectUri: startggConfig.redirectUri,
      });
      sessionStorage.setItem(RETURN_PATH_STORAGE_KEY, window.location.pathname);
      window.location.assign(res.authorizeUrl);
    } catch (e) {
      console.error(e);
      setLinkError(true);
    }
  };

  return (
    <div css={classes.root}>
      {status === "entered" && link ? (
        <div css={[classes.card, classes.cardEntered]}>
          <p css={classes.status}>
            <CheckCircleIcon css={classes.statusIcon} />
            <span>{t("startggEntry.statusEntered")}</span>
          </p>
          <div css={classes.account}>
            <p css={classes.accountLabel}>{t("startggEntry.linkedAccount")}</p>
            <p css={classes.accountName}>{link.username}</p>
          </div>
          <button type="button" onClick={clear} css={classes.subButton}>
            {t("startggEntry.unlinkButton")}
          </button>
        </div>
      ) : null}

      {status === "not-linked" ? (
        <div css={classes.card}>
          <p css={classes.description}>{t("startggEntry.linkDescription")}</p>
          <button type="button" onClick={startLink} css={classes.mainButton}>
            {t("startggEntry.linkButton")}
          </button>
          {linkError && (
            <p css={classes.error}>{t("startggEntry.linkFailed")}</p>
          )}
        </div>
      ) : null}

      {status === "checking" ? (
        <div css={classes.card}>
          <p css={classes.description}>{t("startggEntry.checking")}</p>
        </div>
      ) : null}

      {status === "not-entered" && link ? (
        <div css={classes.card}>
          <div css={classes.account}>
            <p css={classes.accountLabel}>{t("startggEntry.linkedAccount")}</p>
            <p css={classes.accountName}>{link.username}</p>
          </div>
          <p css={classes.statusNotEntered}>
            {t("startggEntry.statusNotEntered")}
          </p>
          <p css={classes.description}>
            {t("startggEntry.notEnteredDescription")}
          </p>
          <button
            type="button"
            onClick={() =>
              window.open(startggRegisterUrl(), "_blank", "noopener,noreferrer")
            }
            css={classes.mainButton}
          >
            {t("startggEntry.registerButton")}
          </button>
          <button
            type="button"
            onClick={recheck}
            disabled={isChecking}
            css={classes.subButton}
          >
            {isChecking
              ? t("startggEntry.checking")
              : t("startggEntry.recheckButton")}
          </button>
          <button type="button" onClick={clear} css={classes.subButton}>
            {t("startggEntry.unlinkButton")}
          </button>
        </div>
      ) : null}

      {status === "failed" ? (
        <div css={classes.card}>
          <p css={classes.error}>{t("startggEntry.checkFailed")}</p>
          <button
            type="button"
            onClick={recheck}
            disabled={isChecking}
            css={classes.mainButton}
          >
            {t("startggEntry.recheckButton")}
          </button>
        </div>
      ) : null}
    </div>
  );
};

const styles = () => {
  return {
    root: css({
      width: "100%",
    }),
    card: css({
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      width: "100%",
      maxWidth: "740px",
      margin: "0 auto",
      padding: "16px",
      backgroundColor: "#f5f5f5",
      border: "1px solid #d5d5d5",
      "@media screen and (min-width: 768px)": {
        padding: "24px",
      },
    }),
    cardEntered: css({
      backgroundColor: "#eaf7f5",
      border: "2px solid #57bfae",
      "@media screen and (min-width: 768px)": {
        borderWidth: "1px",
      },
    }),
    status: css({
      display: "flex",
      gap: "10px",
      alignItems: "center",
      margin: 0,
      fontSize: "16px",
      fontWeight: "bold",
      lineHeight: "22px",
    }),
    statusIcon: css({
      flex: "none",
      fontSize: "24px",
      color: "#00c2ae",
    }),
    statusNotEntered: css({
      margin: 0,
      color: "#c0392b",
      fontSize: "16px",
      fontWeight: "bold",
      lineHeight: "22px",
    }),
    account: css({
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    }),
    accountLabel: css({
      margin: 0,
      color: "#6f6f6f",
      fontSize: "12px",
      lineHeight: "18px",
    }),
    accountName: css({
      margin: 0,
      fontSize: "20px",
      fontWeight: "bold",
      lineHeight: "28px",
    }),
    description: css({
      margin: 0,
      fontSize: "14px",
      lineHeight: "20px",
    }),
    error: css({
      margin: 0,
      color: "#c0392b",
      fontSize: "14px",
      lineHeight: "20px",
    }),
    mainButton: css({
      width: "100%",
      minHeight: "44px",
      padding: "10px 16px",
      color: "#fff",
      fontFamily: "inherit",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "20px",
      backgroundColor: "#0b0b0b",
      border: "none",
      cursor: "pointer",
    }),
    subButton: css({
      width: "100%",
      minHeight: "44px",
      padding: "10px 16px",
      color: "#0b0b0b",
      fontFamily: "inherit",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "20px",
      backgroundColor: "#fff",
      border: "1px solid #0b0b0b",
      cursor: "pointer",
      "&:disabled": {
        color: "#9b9b9b",
        borderColor: "#9b9b9b",
        cursor: "default",
      },
    }),
  };
};
